// Portal público para aliados (armerías referidoras): /armeros (antes /aliados)
// Entran con teléfono + PIN (configurado en Dashboard → Aliados). Desde aquí
// inscriben a sus clientes en cursos (sin cobro en BBTC — el cliente les paga
// a ellos), ven su lista de referidos con asistencia y el estado de cuenta.
// Cambian la fecha hasta 24 horas antes del curso; con menos tiempo solo
// pueden cancelar (penalidad) e inscribir al cliente de nuevo (V3, 2026-09-24).
// En «Mis clientes» buscan al cliente por nombre o teléfono en todos los meses
// para saber cuándo es su curso (2026-09-28).
// «Mi cuenta» (2026-09-28): balance mensual, pago por ACH, cuenta bancaria y
// débito automático opcional; los términos se aceptan la primera vez.
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import axios from 'axios';
import firebaseApp from '../../utils/firebaseConfig';
import Iconify from '../../components/iconify';
import { isPrivateCourse } from '../../utils/privateCourse';
import { matchesSearch } from '../../utils/search';
import { openPartnerInvoice } from '../../utils/partnerInvoice';
import {
  money,
  currentMonthKey,
  monthLabel,
  recentMonthKeys,
  upcomingMonthKeys,
  partnerRowStatus,
  partnerPriceFor,
  partnerCommissionOf,
  partnerPenaltyOf,
  partnerPenaltyCentsOf,
  partnerBillingOf,
  partnerRentalReason,
  summarizePartnerRows,
  settlementStatus,
  formatDueDate,
  PARTNER_CHANGE_HOURS,
} from '../../utils/partners';
import PartnerAccountTab, { TermsDialog, partnerApi } from '../../sections/partners/PartnerAccountTab';

const db = getFirestore(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';
const SESSION_KEY = 'partner_session';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RED_BUTTON = { bgcolor: '#d40100', '&:hover': { bgcolor: '#b00100' } };
// Montos del resumen: nunca se parten en dos líneas (celular)
const AMOUNT = { whiteSpace: 'nowrap', pl: 1 };

const PageWrapper = styled('div')({ minHeight: '100vh', backgroundColor: '#F4F6F8', paddingBottom: 48 });
const Header = styled('div')({ backgroundColor: '#1a1a1a', color: '#fff', padding: '28px 0 24px', marginBottom: 24 });

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('es-PR', { weekday: 'long', day: 'numeric', month: 'long' });
};
// Fecha corta de la tabla; con el año si no es el actual (la búsqueda cubre
// todos los meses)
const shortDate = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  const opts = { day: 'numeric', month: 'short' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('es-PR', opts);
};
const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const apiError = (err, fallback) => (err.response && err.response.data && err.response.data.error) || fallback;
const slotIsFull = (s) => s.available === false || (s.remainingSpots != null && s.remainingSpots <= 0);
// Horas que faltan para el curso (el navegador del armero está en hora de PR)
const hoursUntil = (r) => (new Date(`${r.date}T${r.startTime}:00`).getTime() - Date.now()) / 36e5;

// Fechas con curso abierto: días de la semana habilitados o fechas fijas de
// las reglas de horario (igual que la web de clientes), próximos 90 días.
async function loadCourseDates(categoryId) {
  const snap = await getDocs(query(collection(db, 'availability_rules'), where('categoryId', '==', categoryId)));
  const dows = new Set();
  const fixed = new Set();
  snap.docs.forEach((d) => {
    const r = d.data();
    if (r.active === false) return;
    if (r.date) fixed.add(r.date);
    else if (typeof r.dayOfWeek === 'number') dows.add(r.dayOfWeek);
  });
  const out = [];
  const today = new Date();
  for (let i = 0; i < 90 && out.length < 12; i += 1) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
    const iso = isoOf(d);
    if (dows.has(d.getDay()) || fixed.has(iso)) out.push(iso);
  }
  return out;
}

async function loadSlots(categoryId, date) {
  const res = await axios.post(`${FUNCTIONS_BASE}/getAvailableSlots`, { categoryId, date, laneId: null });
  return res.data.slots || [];
}

const emptyCustomer = { name: '', phone: '', email: '', license: '', licenseExp: '' };

// Cambiar la fecha/hora de una inscripción (mismo curso)
function RescheduleDialog({ row, partner, onClose, onDone }) {
  const [dates, setDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) return undefined;
    let cancelled = false;
    setDate(''); setSlot(null); setSlots([]); setError('');
    setDatesLoading(true);
    loadCourseDates(row.categoryId)
      .then((list) => { if (!cancelled) setDates(list); })
      .catch(() => { if (!cancelled) setDates([]); })
      .finally(() => { if (!cancelled) setDatesLoading(false); });
    return () => { cancelled = true; };
  }, [row]);

  useEffect(() => {
    if (!row || !date) { setSlots([]); return undefined; }
    let cancelled = false;
    setSlot(null);
    setSlotsLoading(true);
    loadSlots(row.categoryId, date)
      .then((list) => { if (!cancelled) setSlots(list); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
  }, [row, date]);

  const handleSave = async () => {
    if (!date || !slot) { setError('Escoge la nueva fecha y el horario.'); return; }
    setSaving(true);
    setError('');
    try {
      await axios.post(`${FUNCTIONS_BASE}/rescheduleReservation`, {
        reservationId: row.id,
        partnerId: partner.id,
        partnerPin: partner.pin,
        newDate: date,
        newStartTime: slot.startTime,
      });
      onDone(`${row.userName} quedó para el ${formatDate(date)} a las ${formatTime(slot.startTime)}. Le llegó la nueva confirmación por email.`);
    } catch (err) {
      setError(apiError(err, 'No se pudo cambiar la fecha.'));
    }
    setSaving(false);
  };

  return (
    <Dialog open={Boolean(row)} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Cambiar fecha</DialogTitle>
      <DialogContent>
        {row && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>{row.userName}</strong> · {row.categoryName} · ahora: {formatDate(row.date)} a las {formatTime(row.startTime)}
          </Typography>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="subtitle2" gutterBottom>Nueva fecha</Typography>
        {datesLoading ? <CircularProgress size={22} /> : (
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            {dates.map((d) => (
              <Chip key={d} label={formatDate(d)} clickable onClick={() => setDate(d)} color={date === d ? 'error' : 'default'} variant={date === d ? 'filled' : 'outlined'} />
            ))}
            {dates.length === 0 && <Typography color="text.secondary">Este curso no tiene fechas abiertas próximamente.</Typography>}
          </Stack>
        )}
        {row && date && (
          <>
            <Typography variant="subtitle2" gutterBottom>Horario</Typography>
            {slotsLoading ? <CircularProgress size={22} /> : (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {slots.map((s) => {
                  const isCurrent = date === row.date && s.startTime === row.startTime;
                  const sel = slot && slot.startTime === s.startTime;
                  return (
                    <Chip
                      key={s.startTime}
                      disabled={isCurrent || slotIsFull(s)}
                      label={`${formatTime(s.startTime)}${isCurrent ? ' (actual)' : s.remainingSpots != null ? ` · ${s.remainingSpots} cupos` : ''}`}
                      clickable
                      onClick={() => setSlot(s)}
                      color={sel ? 'error' : 'default'}
                      variant={sel ? 'filled' : 'outlined'}
                    />
                  );
                })}
                {slots.length === 0 && <Typography color="text.secondary">No hay horarios disponibles ese día.</Typography>}
              </Stack>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cerrar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !slot} sx={RED_BUTTON}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Guardar cambio'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RescheduleDialog.propTypes = {
  row: PropTypes.object,
  partner: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default function PartnerPortalPage() {
  const [partner, setPartner] = useState(null);
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPin, setLoginPin] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [tab, setTab] = useState(0);

  // Inscribir
  const [allCourses, setAllCourses] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [dates, setDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState(null);
  const [customer, setCustomer] = useState(emptyCustomer);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  // Inscribir de nuevo a un cliente cancelado (sus datos ya van llenos)
  const [reenrollFrom, setReenrollFrom] = useState(null);

  // Mis clientes
  const [month, setMonth] = useState(currentMonthKey());
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState('');
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsNotice, setRowsNotice] = useState('');
  const [lastCancelled, setLastCancelled] = useState(null);
  const [rescheduleRow, setRescheduleRow] = useState(null);
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelError, setCancelError] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Mi cuenta (balance, banco, débito automático, términos)
  const [account, setAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);

  const loadAccount = async (p) => {
    if (!p) return;
    setAccountLoading(true);
    try {
      setAccount(await partnerApi(p, 'partnerGetAccount'));
    } catch (e) {
      console.error(e);
    }
    setAccountLoading(false);
  };

  const partnerId = partner ? partner.id : null;
  useEffect(() => { if (partner) loadAccount(partner); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [partnerId]);

  // Al volver de Stripe (microdepósitos / redirect) se abre «Mi cuenta» y se
  // confirma la cuenta bancaria si viene el setup_intent en la URL.
  useEffect(() => {
    if (!partner) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') === 'cuenta') setTab(2);
    const si = params.get('setup_intent');
    if (si) {
      partnerApi(partner, 'partnerConfirmBank', { setupIntentId: si }).then(setAccount).catch(() => null);
      window.history.replaceState({}, '', window.location.pathname);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [partnerId]);

  useEffect(() => {
    let saved = null;
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (raw) saved = JSON.parse(raw);
    } catch (e) { /* ignore */ }
    if (!saved) return;
    setPartner(saved);
    // Refresca precios, comisión y penalidad; si le cambiaron el PIN o la
    // desactivaron, la sesión se cierra.
    axios.post(`${FUNCTIONS_BASE}/partnerLogin`, { phone: saved.phone, code: saved.code, pin: saved.pin })
      .then((res) => {
        const p = { ...res.data.partner, pin: saved.pin };
        setPartner(p);
        try { localStorage.setItem(SESSION_KEY, JSON.stringify(p)); } catch (e) { /* ignore */ }
      })
      .catch((err) => {
        const st = err.response && err.response.status;
        if (st === 403 || st === 404) {
          try { localStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
          setPartner(null);
        }
      });
  }, []);

  useEffect(() => {
    if (!partner) return;
    (async () => {
      const snap = await getDocs(query(collection(db, 'reservation_categories'), where('type', '==', 'course')));
      setAllCourses(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    })();
  }, [partner]);

  // Cursos que la armería puede vender: los activos, menos el privado — ese
  // se coordina directamente con BBTC (idioma, nueva/renovación, instructor).
  const courses = useMemo(
    () => allCourses
      .filter((c) => c.active !== false && !isPrivateCourse(c))
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [allCourses]
  );

  const monthOptions = useMemo(() => [...upcomingMonthKeys(3).reverse(), ...recentMonthKeys(12)], []);

  const handleLogin = async () => {
    const digits = loginPhone.replace(/\D/g, '');
    if (digits.length < 10 || !/^\d{4}$/.test(loginPin)) { setLoginError('Escribe el teléfono (10 dígitos) y el PIN de 4 dígitos.'); return; }
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await axios.post(`${FUNCTIONS_BASE}/partnerLogin`, { phone: digits, pin: loginPin });
      const p = { ...res.data.partner, pin: loginPin };
      setPartner(p);
      localStorage.setItem(SESSION_KEY, JSON.stringify(p));
    } catch (err) {
      setLoginError(apiError(err, 'No se pudo iniciar sesión.'));
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem(SESSION_KEY);
    setPartner(null);
    setAccount(null);
    setLoginPin('');
  };

  useEffect(() => {
    if (!categoryId) { setDates([]); return undefined; }
    let cancelled = false;
    setDatesLoading(true);
    setDate(''); setSlot(null); setSlots([]);
    loadCourseDates(categoryId)
      .then((list) => { if (!cancelled) setDates(list); })
      .catch(() => { if (!cancelled) setDates([]); })
      .finally(() => { if (!cancelled) setDatesLoading(false); });
    return () => { cancelled = true; };
  }, [categoryId]);

  useEffect(() => {
    if (!categoryId || !date) { setSlots([]); return undefined; }
    let cancelled = false;
    setSlotsLoading(true);
    setSlot(null);
    loadSlots(categoryId, date)
      .then((list) => { if (!cancelled) setSlots(list); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
  }, [categoryId, date]);

  const selectedCourse = courses.find((c) => c.id === categoryId);
  const priceCents = partner ? partnerPriceFor(partner, categoryId) : 0;
  const commissionCents = partnerCommissionOf(partner);
  const penaltyCents = partnerPenaltyOf(partner);
  // En B&B el cliente solo paga renta de arma y municiones con licencia
  // nueva, o en renovación si la licencia está vencida el día del curso.
  const rentalReason = partnerRentalReason({
    categoryName: selectedCourse ? selectedCourse.name : '',
    date,
    licenseExpiration: customer.licenseExp,
  });
  const licenseValidForCourse = Boolean(customer.licenseExp && date && customer.licenseExp >= date);

  const resetForm = () => {
    setCategoryId(''); setDate(''); setSlot(null); setSlots([]);
    setCustomer(emptyCustomer);
    setReenrollFrom(null);
  };

  // Inscribir de nuevo a un cliente (cancelado o que no se presentó): mismo
  // curso preseleccionado y sus datos llenos; el armero escoge fecha y hora.
  const startReenroll = (r) => {
    setSuccess(null);
    setSubmitError('');
    setRowsNotice('');
    setLastCancelled(null);
    setCustomer({
      name: r.userName || '',
      phone: String(r.userPhone || '').replace(/\D/g, ''),
      email: r.userEmail || '',
      license: r.licenseNumber || '',
      licenseExp: r.licenseExpiration || '',
    });
    setDate(''); setSlot(null); setSlots([]);
    setCategoryId(courses.some((c) => c.id === r.categoryId) ? r.categoryId : '');
    setReenrollFrom(r);
    setTab(0);
  };

  const handleSubmit = async () => {
    setSubmitError('');
    if (!categoryId || !date || !slot) { setSubmitError('Escoge el curso, la fecha y el horario.'); return; }
    if (!customer.name.trim() || customer.phone.replace(/\D/g, '').length < 10) { setSubmitError('Nombre y teléfono (10 dígitos) del cliente son requeridos.'); return; }
    if (!EMAIL_RE.test(customer.email.trim())) { setSubmitError('Escribe un email válido del cliente: ahí le llega la confirmación.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${FUNCTIONS_BASE}/adminCreateReservation`, {
        categoryId,
        date,
        startTime: slot.startTime,
        customerName: customer.name.trim(),
        customerPhone: customer.phone.replace(/\D/g, ''),
        customerEmail: customer.email.trim().toLowerCase(),
        licenseNumber: customer.license.trim() || null,
        licenseExpiration: customer.licenseExp || null,
        notes: `Referido por ${partner.name}`,
        createdBy: 'partner',
        partnerId: partner.id,
        partnerPin: partner.pin,
      });
      setSuccess({
        code: res.data.confirmationCode,
        name: customer.name.trim(),
        email: customer.email.trim().toLowerCase(),
        course: selectedCourse ? selectedCourse.name : '',
        date,
        time: slot.startTime,
        rentalReason,
        licenseKnown: Boolean(customer.licenseExp),
      });
      resetForm();
    } catch (err) {
      setSubmitError(apiError(err, 'No se pudo inscribir al cliente.'));
    }
    setSubmitting(false);
  };

  const loadRows = async () => {
    if (!partner) return;
    setRowsLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'reservations'), where('partnerId', '==', partner.id)));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => ((b.date || '') + (b.startTime || '')).localeCompare((a.date || '') + (a.startTime || '')));
      setAllRows(list);
    } catch (e) {
      console.error(e);
    }
    setRowsLoading(false);
  };

  useEffect(() => { if (tab === 1) loadRows(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, month, partner]);

  const rows = useMemo(() => allRows.filter((r) => (r.date || '').startsWith(month)), [allRows, month]);
  const summary = useMemo(() => summarizePartnerRows(rows), [rows]);
  // Estado de cuenta ya cerrado de ese mes (si lo hay)
  const settlement = useMemo(() => (account ? account.settlements.find((s) => s.month === month) : null), [account, month]);
  const suspended = Boolean(account && account.suspendedForNonPayment);
  const termsPending = Boolean(account && !account.termsUpToDate);

  // Con algo escrito en el buscador la tabla muestra las inscripciones de ese
  // cliente en TODOS los meses (no solo el del selector); el resumen del mes
  // se esconde mientras tanto.
  const searching = search.trim() !== '';
  const visibleRows = useMemo(
    () => (searching ? allRows.filter((r) => matchesSearch(search, [r.userName, r.userPhone])) : rows),
    [searching, search, allRows, rows]
  );

  // Ventana de cambios del curso: la de la categoría o 24h (igual que el servidor)
  const changeWindowHours = (categoryIdOfRow) => {
    const c = allCourses.find((x) => x.id === categoryIdOfRow);
    const raw = c ? c.rescheduleHoursBefore : null;
    const h = Number(raw);
    return raw !== null && raw !== undefined && raw !== '' && Number.isFinite(h) ? h : PARTNER_CHANGE_HOURS;
  };

  const handleRescheduled = (message) => {
    setRescheduleRow(null);
    setLastCancelled(null);
    setRowsNotice(message);
    loadRows();
  };

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError('');
    try {
      const res = await axios.post(`${FUNCTIONS_BASE}/cancelReservation`, {
        reservationId: cancelRow.id,
        partnerId: partner.id,
        partnerPin: partner.pin,
      });
      const late = Boolean(res.data && res.data.lateCancel);
      const penaltyNote = late ? ` Por ser con menos de ${PARTNER_CHANGE_HOURS} horas se factura la penalidad de ${money(res.data.penaltyCents)}.` : '';
      setRowsNotice(`Se canceló la inscripción de ${cancelRow.userName} (${cancelRow.categoryName}, ${formatDate(cancelRow.date)}).${penaltyNote}`);
      setLastCancelled(cancelRow);
      setCancelRow(null);
      loadRows();
    } catch (err) {
      setCancelError(apiError(err, 'No se pudo cancelar la inscripción.'));
    }
    setCancelling(false);
  };

  // ¿La cancelación de esta fila sería con menos de la ventana (penalidad)?
  const cancelIsLate = cancelRow ? hoursUntil(cancelRow) < changeWindowHours(cancelRow.categoryId) : false;

  if (!partner) {
    return (
      <PageWrapper>
        <Header>
          <Container maxWidth="sm">
            <Typography variant="h4" fontWeight="bold" textAlign="center">B & B Target Center</Typography>
            <Typography variant="body2" textAlign="center" sx={{ opacity: 0.7, mt: 0.5 }}>Portal de aliados</Typography>
          </Container>
        </Header>
        <Container maxWidth="sm">
          <Card sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom textAlign="center">Iniciar sesión</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              Entra con el teléfono y el PIN que te dio BBTC.
            </Typography>
            {loginError && <Alert severity="error" sx={{ mb: 2 }}>{loginError}</Alert>}
            <TextField fullWidth label="Teléfono" value={loginPhone} onChange={(e) => setLoginPhone(e.target.value)} placeholder="7875551234" sx={{ mb: 2 }} inputProps={{ inputMode: 'tel' }} />
            <TextField fullWidth label="PIN" type="password" value={loginPin} onChange={(e) => setLoginPin(e.target.value.replace(/\D/g, '').slice(0, 4))} onKeyDown={(e) => e.key === 'Enter' && handleLogin()} sx={{ mb: 2 }} inputProps={{ inputMode: 'numeric', maxLength: 4 }} />
            <Button fullWidth variant="contained" size="large" onClick={handleLogin} disabled={loginLoading} sx={{ ...RED_BUTTON, py: 1.5 }}>
              {loginLoading ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
            </Button>
          </Card>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Header>
        <Container maxWidth="md">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" fontWeight="bold">{partner.name}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>Portal de aliados · B & B Target Center</Typography>
            </Box>
            <Button color="inherit" size="small" onClick={handleLogout}>Salir</Button>
          </Stack>
        </Container>
      </Header>
      <Container maxWidth="md">
        <Tabs value={tab} onChange={(e, v) => { setTab(v); setSuccess(null); setRowsNotice(''); setLastCancelled(null); if (v === 2) loadAccount(partner); }} sx={{ mb: 2 }} variant="scrollable" allowScrollButtonsMobile>
          <Tab label="Inscribir cliente" />
          <Tab label="Mis clientes" />
          <Tab label={account && account.balanceDueCents > 0 ? `Mi cuenta · ${money(account.balanceDueCents)}` : 'Mi cuenta'} sx={account && account.overdueCents > 0 ? { color: 'error.main', fontWeight: 700 } : undefined} />
        </Tabs>

        {account && tab !== 2 && account.balanceDueCents > 0 && (
          <Alert severity={account.overdueCents > 0 ? 'error' : 'warning'} sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => setTab(2)}>Ver y pagar</Button>}>
            {account.overdueCents > 0
              ? <>Tienes <strong>{money(account.overdueCents)} vencidos</strong> con BBTC.</>
              : <>Tienes un balance de <strong>{money(account.balanceDueCents)}</strong> pendiente con BBTC.</>}
          </Alert>
        )}
        {suspended && tab === 0 && (
          <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => setTab(2)}>Mi cuenta</Button>}>
            Tus inscripciones están <strong>suspendidas por balance vencido</strong>. Paga el balance o comunícate con BBTC.
          </Alert>
        )}

        {tab === 0 && (
          <Card sx={{ p: 3, borderRadius: 3 }}>
            {success && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                <strong>{success.name}</strong> quedó inscrito en <strong>{success.course}</strong> el {formatDate(success.date)} a las {formatTime(success.time)}.
                Código: <strong>{success.code}</strong>. Le llegará la confirmación a {success.email} y podrá verla en la app de BBTC.
                {' '}
                {success.rentalReason === 'expired' && 'Recuérdale: con la licencia vencida paga en B&B la renta de arma y las municiones.'}
                {success.rentalReason === 'new' && 'Recuérdale: en B&B paga la renta de arma y las municiones (licencia nueva).'}
                {!success.rentalReason && success.licenseKnown && 'Con la licencia vigente no paga nada en B&B.'}
                {!success.rentalReason && !success.licenseKnown && 'Si su licencia está vencida el día del curso, paga en B&B la renta de arma y las municiones.'}
              </Alert>
            )}
            {reenrollFrom && !success && (
              <Alert severity="info" sx={{ mb: 3 }} onClose={resetForm}>
                Inscribiendo de nuevo a <strong>{reenrollFrom.userName}</strong>: escoge el curso, la fecha y el horario. Sus datos ya están llenos.
              </Alert>
            )}
            {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>1. Curso</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
              {courses.map((c) => (
                <Chip key={c.id} label={c.name} clickable onClick={() => setCategoryId(c.id)} color={categoryId === c.id ? 'error' : 'default'} variant={categoryId === c.id ? 'filled' : 'outlined'} sx={{ fontWeight: 600 }} />
              ))}
              {courses.length === 0 && <Typography color="text.secondary">No hay cursos activos.</Typography>}
            </Stack>
            {selectedCourse && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: -2 }}>
                Precio acordado con BBTC por este curso: <strong>{money(priceCents)}</strong> (se factura si el cliente asiste, menos {money(commissionCents)} de referido).
              </Typography>
            )}

            {categoryId && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>2. Fecha</Typography>
                {datesLoading ? <CircularProgress size={22} /> : (
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
                    {dates.map((d) => (
                      <Chip key={d} label={formatDate(d)} clickable onClick={() => setDate(d)} color={date === d ? 'error' : 'default'} variant={date === d ? 'filled' : 'outlined'} />
                    ))}
                    {dates.length === 0 && <Typography color="text.secondary">Este curso no tiene fechas abiertas próximamente.</Typography>}
                  </Stack>
                )}
              </>
            )}

            {date && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>3. Horario</Typography>
                {slotsLoading ? <CircularProgress size={22} /> : (
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
                    {slots.map((s) => {
                      const sel = slot && slot.startTime === s.startTime;
                      return (
                        <Chip key={s.startTime} disabled={slotIsFull(s)} label={`${formatTime(s.startTime)}${s.remainingSpots != null ? ` · ${s.remainingSpots} cupos` : ''}`} clickable onClick={() => setSlot(s)} color={sel ? 'error' : 'default'} variant={sel ? 'filled' : 'outlined'} />
                      );
                    })}
                    {slots.length === 0 && <Typography color="text.secondary">No hay horarios disponibles ese día.</Typography>}
                  </Stack>
                )}
              </>
            )}

            {slot && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>4. Datos del cliente</Typography>
                <Stack spacing={2} sx={{ mb: 3 }}>
                  <TextField label="Nombre completo *" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} fullWidth />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Teléfono *" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} fullWidth inputProps={{ inputMode: 'tel' }} helperText="Con este teléfono entra a la app de BBTC" />
                    <TextField label="Email *" type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} fullWidth inputProps={{ inputMode: 'email', autoCapitalize: 'none' }} helperText="Ahí le llega la confirmación" />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Número de licencia" value={customer.license} onChange={(e) => setCustomer({ ...customer, license: e.target.value })} fullWidth />
                    <TextField label="Expiración de licencia" type="date" value={customer.licenseExp} onChange={(e) => setCustomer({ ...customer, licenseExp: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
                  </Stack>
                  {/* Qué paga el cliente en B&B (V3): renta de arma y municiones
                      solo con licencia nueva o con la licencia vencida. */}
                  {rentalReason === 'expired' && (
                    <Alert severity="warning">
                      La licencia estará <strong>vencida</strong> el día del curso: informa al cliente que en B&B paga la <strong>renta de arma y las municiones</strong>.
                    </Alert>
                  )}
                  {rentalReason === 'new' && (
                    <Alert severity="warning">
                      <strong>Licencia nueva:</strong> informa al cliente que en B&B paga la <strong>renta de arma y las municiones</strong>.
                    </Alert>
                  )}
                  {!rentalReason && licenseValidForCourse && (
                    <Alert severity="success">
                      Con la licencia vigente el cliente <strong>no paga nada en B&B</strong>.
                    </Alert>
                  )}
                  {!rentalReason && !licenseValidForCourse && (
                    <Alert severity="warning">
                      Si la licencia del cliente está <strong>vencida</strong> el día del curso, en B&B paga la <strong>renta de arma y las municiones</strong>. Escribe la expiración para saberlo.
                    </Alert>
                  )}
                </Stack>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Cobra el curso en tu negocio: el cliente <strong>no vuelve a pagar el curso en BBTC</strong>. BBTC te factura {money(priceCents)} si asiste, menos {money(commissionCents)} de referido.
                  Si no se presenta, o si cancelas con menos de {PARTNER_CHANGE_HOURS} horas, se factura una <strong>penalidad de {money(penaltyCents)}</strong> (sin comisión).
                </Alert>
                <Button variant="contained" size="large" onClick={handleSubmit} disabled={submitting || suspended} sx={RED_BUTTON}>
                  {submitting ? <CircularProgress size={22} color="inherit" /> : 'Inscribir cliente'}
                </Button>
              </>
            )}
          </Card>
        )}

        {tab === 1 && (
          <Card sx={{ p: 3, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
              <Typography variant="h6">Mis clientes</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  size="small"
                  label="Buscar cliente"
                  placeholder="Nombre o teléfono"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ minWidth: { sm: 260 } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment>,
                    endAdornment: searching ? (
                      <InputAdornment position="end">
                        <IconButton size="small" edge="end" aria-label="Borrar búsqueda" onClick={() => setSearch('')}>
                          <Iconify icon="eva:close-fill" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
                <TextField select size="small" label="Mes" value={month} onChange={(e) => { setMonth(e.target.value); setSearch(''); setRowsNotice(''); }} sx={{ minWidth: 200 }}>
                  {monthOptions.map((k) => <MenuItem key={k} value={k}>{monthLabel(k)}</MenuItem>)}
                </TextField>
              </Stack>
            </Stack>

            {rowsNotice && (
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                onClose={() => { setRowsNotice(''); setLastCancelled(null); }}
                action={lastCancelled ? (
                  <Button color="inherit" size="small" onClick={() => startReenroll(lastCancelled)}>Inscribir de nuevo</Button>
                ) : undefined}
              >
                {rowsNotice}
              </Alert>
            )}

            {searching && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {rowsLoading ? 'Buscando...' : `${visibleRows.length} ${visibleRows.length === 1 ? 'inscripción' : 'inscripciones'} de «${search.trim()}» en todos los meses.`}
              </Typography>
            )}

            {!searching && (<>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              <Chip label={`Inscritos: ${summary.total}`} />
              <Chip label={`Asistieron: ${summary.attended}`} color="success" variant="outlined" />
              <Chip label={`No se presentaron: ${summary.noShow}`} color="error" variant="outlined" />
              <Chip label={`Cancelados: ${summary.cancelled}`} variant="outlined" />
              <Chip label={`Próximos: ${summary.upcoming}`} color="info" variant="outlined" />
            </Stack>

            <Card variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              {summary.courseLines.length === 0 && (
                <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Cursos asistidos (0)</Typography><Typography variant="body2" sx={AMOUNT}>{money(0)}</Typography></Stack>
              )}
              {summary.courseLines.map((l) => (
                <Stack key={`${l.name}-${l.priceCents}`} direction="row" justifyContent="space-between">
                  <Typography variant="body2">Asistieron a {l.name} ({l.count} × {money(l.priceCents)})</Typography>
                  <Typography variant="body2" sx={AMOUNT}>{money(l.subtotalCents)}</Typography>
                </Stack>
              ))}
              <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Comisión por referido ({summary.commissionLabel})</Typography><Typography variant="body2" sx={AMOUNT}>− {money(summary.commissionCents)}</Typography></Stack>
              <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Penalidad por inasistencia ({summary.noShowPenaltyLabel})</Typography><Typography variant="body2" sx={AMOUNT}>+ {money(summary.noShowPenaltyCents)}</Typography></Stack>
              <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Penalidad por cancelar con menos de {PARTNER_CHANGE_HOURS} h ({summary.lateCancelPenaltyLabel})</Typography><Typography variant="body2" sx={AMOUNT}>+ {money(summary.lateCancelPenaltyCents)}</Typography></Stack>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight="bold">Total del mes a pagar a BBTC</Typography>
                <Typography variant="subtitle1" fontWeight="bold">{money(summary.totalCents)}</Typography>
              </Stack>
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={() => openPartnerInvoice({ partner, monthKey: month, rows, settlement })}
                startIcon={<Iconify icon="eva:printer-outline" />}
              >
                Imprimir factura del mes
              </Button>
              {settlement && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                  <Chip size="small" color={settlementStatus(settlement).color} label={`${settlementStatus(settlement).label} — ${money(settlement.status === 'paid' ? settlement.totalCents : settlement.amountDueCents)}`} />
                  {settlement.dueDate && settlement.status !== 'paid' && <Typography variant="caption" color="text.secondary">vence el {formatDueDate(settlement.dueDate)}</Typography>}
                  {['closed', 'failed'].includes(settlement.status) && <Button size="small" onClick={() => setTab(2)}>Pagar</Button>}
                </Stack>
              )}
              {!settlement && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  El mes cierra el último día; después tienes {account ? account.paymentTermDays : 5} días para pagarlo desde «Mi cuenta».
                </Typography>
              )}
            </Card>
            </>)}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Puedes cambiar la fecha o cancelar sin penalidad hasta {PARTNER_CHANGE_HOURS} horas antes del curso.
              Con menos tiempo solo se puede cancelar: se factura la penalidad de {money(penaltyCents)} y, si el cliente quiere otra fecha, lo inscribes de nuevo.
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Curso</TableCell>
                    <TableCell>Asistencia</TableCell>
                    <TableCell align="right">Se factura</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowsLoading && <TableRow><TableCell colSpan={5}>Cargando...</TableCell></TableRow>}
                  {!rowsLoading && visibleRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography color="text.secondary">
                          {searching ? `Ningún cliente inscrito coincide con «${search.trim()}».` : `Sin clientes inscritos en ${monthLabel(month)}.`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {visibleRows.map((r) => {
                    const st = partnerRowStatus(r);
                    const bill = partnerBillingOf(r);
                    const hrs = r.status === 'confirmed' ? hoursUntil(r) : 0;
                    const windowHrs = changeWindowHours(r.categoryId);
                    return (
                      <TableRow key={r.id}>
                        <TableCell>{shortDate(r.date)} {formatTime(r.startTime)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{r.userName}</Typography>
                          <Typography variant="caption" color="text.secondary">{r.userPhone}</Typography>
                        </TableCell>
                        <TableCell>{r.categoryName}</TableCell>
                        <TableCell>
                          <Chip size="small" label={st.label} color={st.color} variant="outlined" />
                          {hrs >= windowHrs && (
                            <Stack direction="row" gap={0.5} sx={{ mt: 0.5 }}>
                              <Button size="small" sx={{ minWidth: 0, px: 1 }} onClick={() => setRescheduleRow(r)}>Cambiar</Button>
                              <Button size="small" color="error" sx={{ minWidth: 0, px: 1 }} onClick={() => { setCancelError(''); setCancelRow(r); }}>Cancelar</Button>
                            </Stack>
                          )}
                          {hrs > 0 && hrs < windowHrs && (
                            <Box sx={{ mt: 0.5 }}>
                              <Button size="small" color="error" sx={{ minWidth: 0, px: 1 }} onClick={() => { setCancelError(''); setCancelRow(r); }}>
                                Cancelar ({money(partnerPenaltyCentsOf(r))})
                              </Button>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Menos de {windowHrs} h: ya no se cambia la fecha
                              </Typography>
                            </Box>
                          )}
                          {(r.status === 'cancelled' || r.status === 'no_show') && (
                            <Button size="small" sx={{ minWidth: 0, px: 1, mt: 0.5, display: 'block' }} onClick={() => startReenroll(r)}>
                              Inscribir de nuevo
                            </Button>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {bill.kind === 'course' && (
                            <>
                              <Typography variant="body2" fontWeight={600}>{money(bill.billedCents)}</Typography>
                              <Typography variant="caption" color="text.secondary">{money(bill.priceCents)} − {money(bill.commissionCents)}</Typography>
                            </>
                          )}
                          {(bill.kind === 'no_show' || bill.kind === 'late_cancel') && (
                            <>
                              <Typography variant="body2" fontWeight={600} color="error.main">{money(bill.billedCents)}</Typography>
                              <Typography variant="caption" color="text.secondary">penalidad</Typography>
                            </>
                          )}
                          {bill.kind === 'none' && money(0)}
                          {bill.kind === 'upcoming' && '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
        {tab === 2 && (
          <PartnerAccountTab partner={partner} account={account} loading={accountLoading} onAccount={setAccount} onReload={() => loadAccount(partner)} />
        )}
      </Container>

      <TermsDialog open={termsPending} partner={partner} account={account} onAccepted={setAccount} onLogout={handleLogout} />

      <RescheduleDialog row={rescheduleRow} partner={partner} onClose={() => setRescheduleRow(null)} onDone={handleRescheduled} />

      <Dialog open={Boolean(cancelRow)} onClose={cancelling ? undefined : () => setCancelRow(null)} fullWidth maxWidth="xs">
        <DialogTitle>Cancelar inscripción</DialogTitle>
        <DialogContent>
          {cancelRow && (
            <Typography variant="body2">
              ¿Cancelar la inscripción de <strong>{cancelRow.userName}</strong> en {cancelRow.categoryName} el {formatDate(cancelRow.date)} a las {formatTime(cancelRow.startTime)}?
              {!cancelIsLate && ` No se factura${cancelRow.userEmail ? ' y al cliente le llega un email con la cancelación' : ''}.`}
            </Typography>
          )}
          {cancelRow && cancelIsLate && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Faltan <strong>menos de {changeWindowHours(cancelRow.categoryId)} horas</strong>: esta cancelación le cuesta a tu armería una
              {' '}<strong>penalidad de {money(partnerPenaltyCentsOf(cancelRow))}</strong> (sin comisión). Si el cliente quiere otra fecha, después lo inscribes de nuevo.
            </Alert>
          )}
          {cancelError && <Alert severity="error" sx={{ mt: 2 }}>{cancelError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelRow(null)} disabled={cancelling}>No</Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={cancelling}>
            {cancelling ? <CircularProgress size={20} color="inherit" /> : (cancelIsLate ? `Sí, cancelar (${money(partnerPenaltyCentsOf(cancelRow))})` : 'Sí, cancelar')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
}
